import {
    ChevronLeft,
    ChevronRight,
    Eye,
    Search,
    Sprout,
    UsersRound,
    X,
} from "lucide-react";
import {
    ChangeEvent,
    useEffect,
    useMemo,
    useState,
} from "react";
import {
    useNavigate,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { getAllInfinityMembers } from "../../../../Redux/Teams/slice";
import { AppDispatch, RootState } from "../../../../store";
import {
    mapInfinityLevelsToMilestones,
} from "./SalarSamriddhiData";

import "./SalarSamriddhiPage.css";

const PAGE_SIZE = 10;

const SalarSamriddhiPage =
    () => {
        const navigate =
            useNavigate();
        const dispatch = useDispatch<AppDispatch>();
        const { infinityMembers = {}, loading } = useSelector(
            (state: RootState) => state.teams
        );
        const samriddhiMilestones = useMemo(
            () => mapInfinityLevelsToMilestones(infinityMembers),
            [infinityMembers]
        );

        const [
            searchValue,
            setSearchValue,
        ] =
            useState("");

        const [
            currentPage,
            setCurrentPage,
        ] =
            useState(1);

        useEffect(() => {
            dispatch(getAllInfinityMembers({ page: 1, limit: PAGE_SIZE }));
        }, [dispatch]);

        const filteredRows = useMemo(() => {
            const search = searchValue.trim().toLowerCase();

            if (!search) {
                return samriddhiMilestones;
            }

            return samriddhiMilestones.filter((record) =>
                record.searchText.includes(search)
            );
        }, [samriddhiMilestones, searchValue]);

        const totalPages =
            Math.max(
                1,
                Math.ceil(
                    filteredRows.length /
                    PAGE_SIZE
                )
            );

        useEffect(
            () => {
                setCurrentPage(
                    (
                        previous
                    ) =>
                        Math.min(
                            previous,
                            totalPages
                        )
                );
            },
            [
                totalPages,
            ]
        );

        const visibleRows =
            useMemo(
                () => {
                    const start =
                        (
                            currentPage -
                            1
                        ) *
                        PAGE_SIZE;

                    return filteredRows.slice(
                        start,
                        start +
                        PAGE_SIZE
                    );
                },
                [
                    filteredRows,
                    currentPage,
                ]
            );

        const pageNumbers =
            useMemo(
                () =>
                    Array.from(
                        {
                            length:
                                totalPages,
                        },
                        (
                            _,
                            index
                        ) =>
                            index +
                            1
                    ),
                [
                    totalPages,
                ]
            );

        const handleSearch =
            (
                event: ChangeEvent<HTMLInputElement>
            ) => {
                setSearchValue(
                    event.target.value
                );

                setCurrentPage(
                    1
                );
            };

        const clearSearch =
            () => {
                setSearchValue(
                    ""
                );

                setCurrentPage(
                    1
                );
            };

        const handleViewNetwork =
            (
                level: number
            ) => {
                navigate(
                    `/LevelWise/${level}`
                );
            };

        return (
            <section className="samriddhi-page">
                <div className="samriddhi-shell">
                    <div className="samriddhi-card">
                        <header className="samriddhi-header">
                            <div className="samriddhi-heading">
                                <span className="samriddhi-icon">
                                    <Sprout
                                        size={
                                            19
                                        }
                                        strokeWidth={
                                            1.9
                                        }
                                    />
                                </span>

                                <div>
                                    <span className="samriddhi-kicker">
                                        MY NETWORK
                                    </span>

                                    <h1>
                                        Salar Samriddhi
                                    </h1>

                                    <p>
                                        Track milestone requirements, contributors and your active Samriddhi network.
                                    </p>
                                </div>
                            </div>

                            <div className="samriddhi-search">
                                <Search
                                    size={
                                        16
                                    }
                                    strokeWidth={
                                        1.9
                                    }
                                />

                                <input
                                    type="text"
                                    inputMode="search"
                                    value={
                                        searchValue
                                    }
                                    onChange={
                                        handleSearch
                                    }
                                    placeholder="Search milestones"
                                    aria-label="Search Salar Samriddhi milestones"
                                />

                                {searchValue && (
                                    <button
                                        type="button"
                                        onClick={
                                            clearSearch
                                        }
                                        aria-label="Clear search"
                                    >
                                        <X
                                            size={
                                                14
                                            }
                                            strokeWidth={
                                                2.2
                                            }
                                        />
                                    </button>
                                )}
                            </div>
                        </header>

                        <div className="samriddhi-summary-row">
                            <div className="samriddhi-summary">
                                <span>
                                    Total Milestones
                                </span>

                                <strong>
                                    {
                                        filteredRows.length
                                    }
                                </strong>
                            </div>

                            <div className="samriddhi-summary">
                                <span>
                                    Completed
                                </span>

                                <strong>
                                    {
                                        filteredRows.filter(
                                            (
                                                row
                                            ) =>
                                                row.status ===
                                                "completed"
                                        ).length
                                    }
                                </strong>
                            </div>
                        </div>

                        <div className="samriddhi-table-shell">
                            <div
                                className="samriddhi-table-scroll"
                                role="region"
                                aria-label="Salar Samriddhi milestones table"
                                tabIndex={
                                    0
                                }
                            >
                                <table className="samriddhi-table samriddhi-main-table">
                                    <thead>
                                        <tr>
                                            <th
                                                scope="col"
                                                className="samriddhi-sticky-column"
                                            >
                                                Milestone
                                            </th>

                                            <th scope="col">
                                                Contribution Requirement
                                            </th>

                                            <th scope="col">
                                                Active Contributors
                                            </th>

                                            <th scope="col">
                                                Status
                                            </th>

                                            <th scope="col">
                                                Network
                                            </th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td
                                                    colSpan={
                                                        5
                                                    }
                                                    className="samriddhi-empty"
                                                >
                                                    <strong>
                                                        Loading...
                                                    </strong>
                                                </td>
                                            </tr>
                                        ) : visibleRows.length >
                                            0 ? (
                                            visibleRows.map(
                                                (
                                                    record
                                                ) => (
                                                    <tr
                                                        key={
                                                            record.id
                                                        }
                                                    >
                                                        <td className="samriddhi-sticky-column">
                                                            <span className="samriddhi-table-primary">
                                                                {
                                                                    record.milestone
                                                                }
                                                            </span>
                                                        </td>

                                                        <td>
                                                            <span className="samriddhi-number-value">
                                                                {
                                                                    record.contributionRequirement
                                                                }
                                                            </span>
                                                        </td>

                                                        <td>
                                                            <span className="samriddhi-number-value">
                                                                {
                                                                    record.activeContributors
                                                                }
                                                            </span>
                                                        </td>

                                                        <td>
                                                            <span
                                                                className={`samriddhi-status ${record.status
                                                                    }`}
                                                            >
                                                                {record.status ===
                                                                    "completed"
                                                                    ? "Completed"
                                                                    : "Pending"}
                                                            </span>
                                                        </td>

                                                        <td>
                                                            <button
                                                                type="button"
                                                                className="samriddhi-view-button"
                                                                onClick={() =>
                                                                    handleViewNetwork(
                                                                        record.level
                                                                    )
                                                                }
                                                            >
                                                                <span>
                                                                    View
                                                                </span>

                                                                <Eye
                                                                    size={
                                                                        13
                                                                    }
                                                                    strokeWidth={
                                                                        2
                                                                    }
                                                                />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                )
                                            )
                                        ) : (
                                            <tr>
                                                <td
                                                    colSpan={
                                                        5
                                                    }
                                                    className="samriddhi-empty"
                                                >
                                                    <UsersRound
                                                        size={
                                                            27
                                                        }
                                                        strokeWidth={
                                                            1.7
                                                        }
                                                    />

                                                    <strong>
                                                        No milestones found
                                                    </strong>

                                                    <span>
                                                        Try a different search term.
                                                    </span>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <footer className="samriddhi-footer">
                            <span className="samriddhi-result-count">
                                Showing{" "}
                                <strong>
                                    {visibleRows.length >
                                        0
                                        ? (
                                            currentPage -
                                            1
                                        ) *
                                        PAGE_SIZE +
                                        1
                                        : 0}
                                </strong>{" "}
                                -{" "}
                                <strong>
                                    {Math.min(
                                        currentPage *
                                        PAGE_SIZE,
                                        filteredRows.length
                                    )}
                                </strong>{" "}
                                of{" "}
                                <strong>
                                    {
                                        filteredRows.length
                                    }
                                </strong>
                            </span>

                            <div className="samriddhi-pagination">
                                <button
                                    type="button"
                                    className="samriddhi-pagination-arrow"
                                    disabled={
                                        currentPage ===
                                        1
                                    }
                                    onClick={() =>
                                        setCurrentPage(
                                            (
                                                previous
                                            ) =>
                                                Math.max(
                                                    1,
                                                    previous -
                                                    1
                                                )
                                        )
                                    }
                                    aria-label="Previous page"
                                >
                                    <ChevronLeft
                                        size={
                                            16
                                        }
                                        strokeWidth={
                                            2
                                        }
                                    />
                                </button>

                                {pageNumbers.map(
                                    (
                                        page
                                    ) => (
                                        <button
                                            type="button"
                                            className={`samriddhi-pagination-page ${currentPage ===
                                                    page
                                                    ? "active"
                                                    : ""
                                                }`}
                                            key={
                                                page
                                            }
                                            onClick={() =>
                                                setCurrentPage(
                                                    page
                                                )
                                            }
                                            aria-current={
                                                currentPage ===
                                                    page
                                                    ? "page"
                                                    : undefined
                                            }
                                        >
                                            {
                                                page
                                            }
                                        </button>
                                    )
                                )}

                                <button
                                    type="button"
                                    className="samriddhi-pagination-arrow"
                                    disabled={
                                        currentPage ===
                                        totalPages
                                    }
                                    onClick={() =>
                                        setCurrentPage(
                                            (
                                                previous
                                            ) =>
                                                Math.min(
                                                    totalPages,
                                                    previous +
                                                    1
                                                )
                                        )
                                    }
                                    aria-label="Next page"
                                >
                                    <ChevronRight
                                        size={
                                            16
                                        }
                                        strokeWidth={
                                            2
                                        }
                                    />
                                </button>
                            </div>
                        </footer>
                    </div>
                </div>
            </section>
        );
    };

export default SalarSamriddhiPage;
